import { Toolbar } from "../../components/Toolbar";
import { AdministrateTable } from "../components/AdministrateTable";
import { LabOptionsFilter } from "../components/filters/LabOptionsFilter";
import { PayorOptionsFilter } from "../components/filters/PayorOptionsFilter";
import { SystemConfigLayout } from "../components/SystemConfigLayout";
import { DESTROY, makeColumns, UPDATE, useAdministrateTable, useCreatePermission } from "../utils";
import { CountCell } from "../../components/grid/cells/CountCell";
import { Routes } from "../Routes";
import { AdministrateEnumFilter } from "../components/AdministrateEnumFilter";
import { SpecialProjectCaseAssignmentActionCell } from "./components/SpecialProjectCaseAssignmentActionCell";
import { Badge } from "reactstrap";
import { EnumFilter } from "../../components/inputs";
import { CreateSpecialProjectCaseAssignmentModal } from "./components/CreateSpecialProjectCaseAssignmentModal";
import { useCallback } from "react";
import { Link } from "react-router";

const caseTypeOptions = [
  { label: "Lab", value: "lab" },
  { label: "Pharmacy", value: "pharmacy" },
];

const AccountsCell = ({ accounts }) => (
  <>
    {accounts && accounts?.length > 0 ? (
      <div className="d-flex flex-column">
        {accounts.map((account) => (
          <Link key={account.id} target="_blank" to={Routes.user.url(account?.id)}>
            <span className="token">{account?.email || "None"}</span>
          </Link>
        ))}
      </div>
    ) : (
      <span className="token">No User Attached</span>
    )}
  </>
);

const CaseTypeCell = ({ value }) => <Badge color="primary">{value}</Badge>;

const useColumns = makeColumns(() => [
  {
    id: "id",
    accessor: "id",
    Header: "ID",
    maxWidth: 70,
  },
  {
    id: "payors.id",
    accessor: (row) => {
      return row?.payors?.map((payor) => payor?.name);
    },
    Header: "Payors",
    Cell: ({ value }) => (
      <div title={value}>
        <CountCell count={value?.length} label="Payors" />
      </div>
    ),
    Filter: (props) => <PayorOptionsFilter {...props} isMulti />,
    disableSortBy: true,
    maxWidth: 100,
  },
  {
    id: "lab",
    accessor: "lab",
    Header: "Lab",
    Filter: (props) => <LabOptionsFilter valueKey="id" {...props} />,
    Cell: ({ _row, value }) => {
      if (value) {
        return (
          <Link to={Routes.lab.url(value?.id)} target="_blank">
            <span className="token">{value?.labName}</span>
          </Link>
        );
      } else {
        return <span className="token">No Lab Attached</span>;
      }
    },
  },
  {
    id: "tests.id",
    accessor: (row) => {
      return row?.tests?.map((test) => test?.commercial_name);
    },
    Header: "Test(s)",
    Cell: ({ value }) => (
      <div title={value}>
        <CountCell count={value?.length} label="Test" />
      </div>
    ),
    Filter: (props) => <AdministrateEnumFilter {...props} isMulti resourceUrl="/admin/tests.json" valueKey="id" />,
  },
  {
    id: "servicing_provider_type",
    accessor: "servicingProviderType",
    Header: "Case Type",
    Cell: CaseTypeCell,
    Filter: (props) => <EnumFilter {...props} options={caseTypeOptions} />,
    maxWidth: 70,
  },
  {
    id: "accounts",
    accessor: "accounts",
    Header: "Accounts",
    Cell: ({ value }) => <AccountsCell accounts={value} />,
    Filter: (props) => (
      <AdministrateEnumFilter {...props} resourceUrl="/admin/accounts.json" valueKey="id" labelKey="email" searchParam="email" />
    ),
  },
  {
    id: "actions",
    Header: "Actions",
    Cell: SpecialProjectCaseAssignmentActionCell,
    disableFilters: true,
    disableSortBy: true, //action cell
    minWidth: 220,
  },
]);

export const SpecialProjectCaseAssignmentIndex = () => {
  const { tableRef, allFilters, setTableFilters } = useAdministrateTable();

  const [canCreate] = useCreatePermission("SpecialProjectCaseAssignmentConfig");

  const columns = useColumns();

  const onAdd = useCallback(
    (message, errorDetails = []) => {
      tableRef.current?.refresh?.();
      tableRef.current?.setMessage?.(message, errorDetails.length ? "warning" : "success", errorDetails);
    },
    [tableRef]
  );

  return (
    <SystemConfigLayout title={["Assignment Automation", "Special Project Case Assignment"]}>
      <Toolbar className="align-items-center mb-3">
        {canCreate && <CreateSpecialProjectCaseAssignmentModal onAdd={onAdd} color="primary" />}
      </Toolbar>

      <AdministrateTable
        resourceUrl={"/admin/special_project_case_assignment_configs.json"}
        resourceName={"special_project_case_assignment_configs"}
        permissions={[UPDATE, DESTROY]}
        onFilteredChange={setTableFilters}
        columns={columns}
        ref={tableRef}
      />
    </SystemConfigLayout>
  );
};
